import { inject, Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { map } from 'rxjs/operators';

import { filterBoolean } from '@core/common-legacy';
import {
  IntelligenceSettingsActions,
  IntelligenceSettingsSelectors,
  selectIntelligenceFeatureSettings,
  selectIntelligenceMasterEnabled,
} from '@core/intelligence-data';
import { AnyIntelligenceSettings, IntelligenceFeatureSettings } from '@core/intelligence-types';

@Injectable({ providedIn: 'root' })
export class IntelligenceSettingsDataService {
  private readonly store = inject(Store);
  private readonly settingsState$ = this.store.select(IntelligenceSettingsSelectors.state);
  private readonly settings$ = this.store.select(selectIntelligenceFeatureSettings);

  public readonly settingsState = this.store.selectSignal(IntelligenceSettingsSelectors.state);
  public readonly settings = this.store.selectSignal(selectIntelligenceFeatureSettings);
  public readonly masterEnabled = this.store.selectSignal(selectIntelligenceMasterEnabled);
  public readonly masterEnabled$ = this.store.select(selectIntelligenceMasterEnabled);
  public settingsLoaded$ = this.settingsState$.pipe(map(state => state.loaded));

  public toggleAiFeature<T extends AnyIntelligenceSettings['field']>(key: T, value: boolean) {
    this.store.dispatch(IntelligenceSettingsActions.toggleFeature({ payload: { [key]: value } }));
  }

  public setMasterEnabled(enabled: boolean) {
    this.store.dispatch(IntelligenceSettingsActions.toggleFeature({ payload: { enabled } }));
  }

  public updateFeatureSettings(settings: Partial<IntelligenceFeatureSettings>) {
    this.store.dispatch(IntelligenceSettingsActions.updateFeatureSettings({ payload: settings }));
  }

  public getFeatureState<K extends AnyIntelligenceSettings['feature']>(feature: K) {
    return this.settings$.pipe(
      filterBoolean,
      map(
        data =>
          <Extract<AnyIntelligenceSettings, { feature: K }>>(
            data.find(setting => setting.feature === feature)!
          ),
      ),
    );
  }

  public load() {
    this.store.dispatch(IntelligenceSettingsActions.fetch());
  }

  public fetchDisqualifyReasons(appGuid: string) {
    this.store.dispatch(
      IntelligenceSettingsActions.fetchDisqualifyReasons({ payload: { appGuid } }),
    );
  }

  public fetchIfNotLoaded() {
    const { pending, loaded } = this.settingsState();
    if (loaded || pending) return;

    this.load();
  }
}
